import { AdjustmentDeliveryNoteStatusInversed } from '@teinor/erp/types/company/order/adjustmentDeliveryNote'
import { permissionCheck } from '../../../../../../../../../baseComponents/PermissionChecker/function'
import { ITInputQuantity } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Inputs'
import { InputTableLayoutStepProps } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { CustomRenderString } from '../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import OrderAdjustmentDeliveryNoteLayout from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderAdjustmentDeliveryNoteLayout'
import { CustomRenderPriceWithMargin, CustomRenderSpecificPrice } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderBuyLayout/CustomRenders'
import { ColumnOrderTableSchema, OrderData } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import { ProductExtendedTyInputTableListing } from '../../../../../../../../../specificComponents/Product/types'
import useAdjustmentDeliveryNoteHeaderURLs from '../../../../../../../../../utils/hooks/adjustmentDeliveryNote/useAdjustmentDeliveryNoteHeaderURLs'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { getAllSpecificPriceRules, getAllWorkingCenters, getUserPermissions } from '../../../../../../../../../utils/reducers/getters'
import { AdjustmentDeliveryNoteProductModelTypeExtendedWithStock } from '../../../types'
import AdjustmentDeliveryNoteCreateStepHeaderDecider from './AdjustmentDNCreateStepHeader'
import AdjustmentDeliveryNoteCreateStepITHeader from './AdjustmentDNCreateStepITHeader'


type AdjustmentDeliveryNoteCreateStepProps = InputTableLayoutStepProps &{ }

const AdjustmentDeliveryNoteCreateStep: React.FC<AdjustmentDeliveryNoteCreateStepProps> = ({ inputTableURLs, completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, userPermissions, headerChanges }) => {
    const { t } = useTwinTranslation()
    const workingCenters = getAllWorkingCenters()
    const orderHeaderURLs = useAdjustmentDeliveryNoteHeaderURLs()
    const specificPriceRules = getAllSpecificPriceRules()

    const columns: ColumnOrderTableSchema<ProductExtendedTyInputTableListing & AdjustmentDeliveryNoteProductModelTypeExtendedWithStock> = [
        {
            id: 'nLine',
            dataKey: 'nLine',
            label: t('line', 'Línea'),
            width: 70,
            hideColumnByDefault: true
        },
        {
            id: 'url',
            dataKey: 'url',
            label: t('image', 'Imagen'),
            component: 'ITDisplayImage',
            width: 70
        },
        {
            id: 'code_bar',
            dataKey: 'code_bar',
            label: t('codeBarShort', 'Cod. de barras'),
            component: 'OrderSearchProductInput',
            field: 'code_bar',
            typeOnChange: 'onChangeAdd',
            sortable: true,
            hideColumnByDefault: true,
        },
        {
            id: 'reference',
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
            component: 'OrderSearchProductInput',
            field: 'reference',
            typeOnChange: 'onChangeAdd',
            sortable: true,
            minWidth: 100,
            hideColumnByDefault: true,
            className: 'quote_order_reference_name'
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            component: 'OrderSearchProductInputName',
            field: 'name',
            typeOnChange: 'onChangeAdd',
            sortable: true,
            hideColumnByDefault: true,
            className: 'quote_order_reference_name'
        },
        {
            id: 'referenceAndName',
            dataKey: 'name',
            label: t('referenceName', 'Referencia y nombre'),
            component: 'OrderSearchProductInputReferenceAndName',
            field: 'referenceAndName',
            typeOnChange: 'onChangeAdd',
            sortable: true,
            minWidth: 180,
            className: 'quote_order_reference_name'
        },
        {
            id: 'editProduct',
            dataKey: 'ProductId',
            label: '',
            simpleLabel: t('buttonSeeProduct', 'Botón de ver producto'),
            component: 'OrderEditProduct',
            width: 35,
            havePermission: permissionCheck(getUserPermissions('product.profile.main'), 'read'),
        },
        {
            id: 'descriptionDocuments',
            dataKey: 'descriptionDocuments',
            label: t('description', 'Descripción'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'shortDescriptionDocuments',
            dataKey: 'shortDescriptionDocuments',
            label: t('shortDescriptionS', 'Desc. corta'),
            simpleLabel: t('shortDescription', 'Descripción corta'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'CopyProduct.name',
            dataKey: 'name',
            label: '',
            simpleLabel: t('editProductName', 'Editar nombre del producto'),
            component: 'ITEditableName',
            typeOnChange: 'onChangeFetch',
            width: 50,
        },
        {
            id: 'notes',
            dataKey: 'notes',
            label: t('notes', 'Notas'),
            component: 'ITEditableText',
            typeOnChange: 'onChangeFetch'
        },
        {
            id: 'unique_number',
            dataKey: 'unique_number',
            label: t('uniqueNumberShort', 'N. único'),
            simpleLabel: t('uniqueNumber', 'Número único'),
            toolTip: t('uniqueNumber', 'Número único'),
            component: 'OrderUniqueNumberProduct',
            hideColumnByDefault: true
        },
        {
            id: 'initialUnitPriceWithoutTax',
            dataKey: 'initialUnitPriceWithoutTax',
            simpleLabel: t('price', 'Precio'),
            label: () => <div className='text-right w-full mr-10'>{t('price', 'Precio')}</div>,
            component: 'ITInputPrice',
            typeOnChange: 'onChangeTotalsFetch',
            width: 120,
            sortable: true
        },
        {
            id: 'Product.SpecificPriceRuleId',
            dataKey: 'Product.SpecificPriceRuleId',
            label: (props) => {
                const myValue = parseInt(props.selectedFilterValue || props.value || specificPriceRules?.defaultSpecificPriceRuleId)
                return <span>{t('pvp', 'PVP') + ' (' + (specificPriceRules?.dictionary?.[myValue]?.name || t('notSelectedPriceRule', 'Sin tarifa selec.')) + ')'} </span>
            },
            simpleLabel: t('sellPrice', 'Precio de venta'),
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: specificPriceRules?.dictionary || {}, label: t('specificPriceRule', 'Tarifa') }
            },
            customRender: (parameterValue, allRowData) => <CustomRenderSpecificPrice specificPriceRules={specificPriceRules} allRowData={allRowData} />,
            hideColumnByDefault: true
        },
        {
            id: 'Product.SpecificPriceRuleIdActual',
            dataKey: 'Product.SpecificPriceRuleId',
            label: t('costPriceWithMarginShort', 'PCM'),
            simpleLabel: t('costPriceWithMargin', 'Precio coste con margen'),
            toolTip: t('costPriceWithMargin', 'Precio coste con margen'),
            customRender: (parameterValue, allRowData) => <CustomRenderPriceWithMargin allRowData={allRowData} CurrencyId={completeData?.header?.CurrencyId} />,
            hideColumnByDefault: true
        },
        {
            id: 'margin',
            dataKey: 'margin',
            label: t('margin', 'Margen'),
            component: 'ITInputPercentage',
            typeOnChange: 'onChangeTotalsFetch',
            hideColumnByDefault: true,
            width: 100,
        },
        {
            id: 'quantity',
            dataKey: 'quantity',
            label: t('quantity', 'Cantidad'),
            component: 'ITInputQuantity',
            typeOnChange: 'onChangeTotalsFetch',
            width: 100,
            skipMinQuantity: true,
            allowNegatives: true
        },
        {
            id: 'quantity2',
            dataKey: 'quantity2',
            label: t('quantitySecondRes', 'Cant. sec.'),
            simpleLabel: t('quantitySecondary', 'Cantidad secundaria'),
            component: 'ITInputQuantity2',
            width: 100,
            typeOnChange: 'onChangeTotalsFetch',
            hideColumnByDefault: true
        },
        {
            id: 'wasteWeight',
            dataKey: 'wasteWeight',
            label: t('wasteWeight', 'Merma'),
            component: 'ITInputPercentage',
            typeOnChange: 'onChangeTotalsFetch',
            width: 100,
            hideColumnByDefault: true,
            allowEmpty: true
        },
        {
            id: 'calculatedQuantity',
            dataKey: 'calculatedQuantity',
            label: t('quantityCalculatedS', 'Cant. calc.'),
            toolTip: t('quantityCalculated', 'Cantidad calculada'),
            simpleLabel: t('quantityCalculated', 'Cantidad calculada'),
            component: 'ITInputQuantity',
            width: 110,
            typeOnChange: 'onChangeTotalsFetch',
            sortable: true,
            hideColumnByDefault: true,
            readOnlyFnc: () => true
        },
        {
            id: 'rowPriceWithoutTaxAndTotalDiscount',
            dataKey: 'rowPriceWithoutTaxAndTotalDiscount',
            label: () => <div className='text-right w-full'>{t('priceTotal', 'Precio Total')}</div>,
            simpleLabel: t('priceTotal', 'Precio Total'),
            component: 'ITDisplayEditFinalPriceBase',
            typeOnChange: 'onChangeTotalsFetch',
            with_vat: false,
            width: 120
        },
        {
            id: 'rowPriceWithTaxAndTotalDiscount',
            dataKey: 'rowPriceWithTaxAndTotalDiscount',
            label: () => <div className='text-right w-full mr-10'>{t('totalPriceWithTaxShort', 'Precio Total Imp.')}</div>,
            simpleLabel: t('totalPriceWithTaxShort', 'Precio Total Imp.'),
            component: 'ITDisplayEditFinalPriceBase',
            typeOnChange: 'onChangeTotalsFetch',
            with_vat: true,
            width: 145,
            hideColumnByDefault: true,
        },
        {
            id: 'totalWithTaxWithComplements',
            dataKey: 'totalWithTaxWithComplements',
            simpleLabel: t('totalWithComplementsAndVat', 'Total con complementos e imp.'),
            label: () => <div className='text-right w-full' >{t('withComplementsAndVatS', 'Con comp. e imp.')}</div>,
            component: 'ITDisplayPrice',
            width: 160,
            hideColumnByDefault: true,
            toolTip: t('totalWithComplementsAndVatL', 'Total con complementos e impuestos'),
        },
        {
            id: 'totalWithoutTaxWithComplements',
            dataKey: 'totalWithoutTaxWithComplements',
            simpleLabel: t('totalWithComplements', 'Total con complementos'),
            label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('totalWithComplements', 'Total con complementos')
        },
        {
            id: 'totalWithoutTaxWithComplementsPrev',
            dataKey: 'totalWithoutTaxWithComplementsPrev',
            simpleLabel: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de desc.'),
            label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de descuentos'),
        },
        {
            id: 'unitPriceWithComplements',
            dataKey: 'unitPriceWithComplements',
            simpleLabel: t('unitPriceWithComplements', 'Precio unitario con complementos'),
            label: () => <div className='text-right w-full'> {t('unitPricewithComplementsS', 'P. U. comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('unitPriceWithComplements', 'Precio unitario con complementos'),
        },
        {
            id: 'editAddonProduct',
            dataKey: 'ProductId',
            label: '',
            simpleLabel: t('buttonAddAddonProduct', 'Botón de añadir complemento'),
            component: 'OrderEditAddonProduct',
            width: 33,
            completeData: completeData,
        },
        {
            id: 'WarehouseId',
            dataKey: 'WarehouseId',
            label: t('warehouse', 'Almacén'),
            component: 'ITInputSelectWarehouses',
            items: workingCenters?.[completeData?.header?.WorkingCenterId]?.dictWarehouses || {},
            typeOnChange: 'onChangeUpdateFetch',
            minWidth: 120,
            addAnyWarehouses: false
        },
        {
            id: 'currentStock',
            dataKey: 'currentStock',
            label: t('currentStockProduct', 'Stock actual'),
            width: 100,
            customRender: (parameterValue: any, allRowData) => <ITInputQuantity allRowData={allRowData} value={parameterValue || 0} readOnlyFnc={() => true} onChange={() => { }} dataKey={'currentStock'} allowNegatives={true} />,
        },
        {
            id: 'finalStock',
            dataKey: 'finalStock',
            label: t('predictedStockProduct', 'Stock previsto'),
            width: 110,
            customRender: (parameterValue, allRowData) => <ITInputQuantity allRowData={allRowData} value={(parseFloat(allRowData?.currentStock || '0') + parseFloat(allRowData?.quantity)) || 0} readOnlyFnc={() => true} onChange={() => { }} dataKey={'finalStock'} allowNegatives={true} />,
        },
        {
            id: 'PrintTags',
            dataKey: 'ProductId',
            label: '',
            simpleLabel: t('printTags', 'Imprimir etiquetas'),
            width: 33,
            component: 'OrderPrintTags',
            hideColumnByDefault: true
        },
        {
            id: 'deleteRow',
            dataKey: 'ProductId',
            label: '',
            simpleLabel: t('deleteLine', 'Eliminar línea'),
            width: 33,
            component: 'ITDeleteRow',
            requiredColumn: true
        },
        {
            id: 'dragLine',
            dataKey: 'nLine',
            label: '',
            simpleLabel: t('moveLine', 'Mover línea'),
            width: 33,
            component: 'ITDragHandle',
            requiredColumn: true
        },
    ]
    return (
        <OrderAdjustmentDeliveryNoteLayout modelId='AdjustmentDeliveryNoteId' name='adjustmentDeliveryNoteCreateStep' translations={{ headerTitle: t('adjustmentDeliveryNote', 'Albarán de regularización') }} className='adjustmentDeliveryNoteCreate' buttonSave={
            permissionCheck(getUserPermissions('adjustmentDeliveryNote'), 'update')
                ?
                <AdjustmentDeliveryNoteCreateStepHeaderDecider orderStatus={completeData?.header.status} OrderLogs={completeData?.header?.OrderLogs} onSubmit={getCompleteData} data={completeData as OrderData} {...{ orderHeaderURLs, setCompleteData, updateHeaderData: inputTableURLs?.updateHeaderData, userPermissions }} />
                : undefined
        } InputTableHeaderChildren={AdjustmentDeliveryNoteCreateStepITHeader} {...{ columns, inputTableURLs, orderHeaderURLs, completeData, setCompleteData, getCompleteData, setTableConfiguration, tableConfiguration, userPermissions, headerChanges, orderStatusDict: AdjustmentDeliveryNoteStatusInversed, hideFinancialRows: true} } />
    )
}

export default AdjustmentDeliveryNoteCreateStep